import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Mail, ArrowRight, Settings } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSEO from "@/lib/seo";

export default function BerufsverkehrTickerConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, ok: false, message: "", already: false, manage_url: "" });
  const sentRef = useRef(false);

  useSEO({
    title: "Berufsverkehr-Ticker bestätigen",
    description: "Bestätigen Sie Ihre Anmeldung zum Berufsverkehr-Ticker.",
    path: "/berufsverkehr-ticker/bestaetigt",
  });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, ok: false, message: "Kein Bestätigungs-Token übergeben.", already: false, manage_url: "" });
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    api
      .get(`/commuter/confirm/${encodeURIComponent(token)}`)
      .then((r) =>
        setState({
          loading: false,
          ok: true,
          message: r.data.message || "Bestätigt.",
          already: !!r.data.already,
          manage_url: r.data.manage_url || "",
        }),
      )
      .catch((e) =>
        setState({
          loading: false,
          ok: false,
          message: formatApiError(e.response?.data?.detail) || "Bestätigung fehlgeschlagen.",
          already: false,
          manage_url: "",
        }),
      );
  }, [token]);

  const manageRelative = (() => {
    if (!state.manage_url) return "";
    try {
      const u = new URL(state.manage_url);
      return u.pathname + u.search;
    } catch {
      return state.manage_url;
    }
  })();

  return (
    <div data-testid="commuter-confirm-page">
      <section className="relative bg-neutral-50 overflow-hidden">
        <div className="container-narrow pt-16 lg:pt-24 pb-16 lg:pb-24 relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 mb-5">
            <Mail className="h-4 w-4 text-[hsl(var(--brand-green-dark))]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-dark))]">Berufsverkehr-Ticker · Bestätigung</span>
          </div>

          {state.loading ? (
            <>
              <h1 className="font-semibold text-3xl sm:text-4xl mb-3">Einen Moment bitte…</h1>
              <p className="text-base text-neutral-600">Wir prüfen Ihren Bestätigungslink.</p>
            </>
          ) : state.ok ? (
            <>
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border-2 border-[hsl(var(--brand-green))] mb-6">
                <CheckCircle2 className="h-7 w-7 text-[hsl(var(--brand-green-strong))]" strokeWidth={1.8} />
              </div>
              <h1 className="font-semibold text-4xl sm:text-5xl leading-[1.1] mb-5" data-testid="commuter-confirm-success">
                {state.already ? "Bereits aktiv — danke!" : "Ihr Berufsverkehr-Ticker ist aktiv!"}
              </h1>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl mb-8">
                {state.already
                  ? "Diese Anmeldung war bereits bestätigt. Ab Ihrem nächsten gewählten Wochentag erhalten Sie automatisch die passende Route in Ihr Postfach."
                  : "Vielen Dank für Ihre Bestätigung. Ab Ihrem nächsten gewählten Wochentag erhalten Sie 10 Minuten vor Abfahrt Ihre persönliche Routenempfehlung."}
              </p>
              <div className="flex flex-wrap gap-3">
                {manageRelative && (
                  <Link
                    to={manageRelative}
                    data-testid="commuter-confirm-manage"
                    className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
                  >
                    <Settings className="h-4 w-4" /> Einstellungen verwalten
                  </Link>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 border border-brand-line bg-white text-neutral-800 px-5 py-3.5 rounded-full hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Zur Startseite <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border-2 border-neutral-300 mb-6">
                <XCircle className="h-7 w-7 text-neutral-600" strokeWidth={1.8} />
              </div>
              <h1 className="font-semibold text-4xl sm:text-5xl leading-[1.1] mb-5" data-testid="commuter-confirm-error">
                Bestätigung nicht möglich
              </h1>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-2xl mb-8">{state.message}</p>
              <Link
                to="/berufsverkehr-ticker"
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white pl-6 pr-5 py-3.5 rounded-full hover:bg-[hsl(var(--brand-green-strong))] transition-colors text-sm font-medium"
              >
                Erneut anmelden <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
